/**
 * aides-particuliers.ts
 * ─────────────────────────────────────────────────────────────────
 * Configuration centralisée des pages "stubs" /particuliers/aides/[slug].
 *
 * Chaque entrée décrit une aide à la rénovation énergétique
 * accessible aux particuliers. Les contenus restent prudents
 * (formulations "jusqu'à", "selon votre profil").
 */

export interface AideStubKeyPoint {
  iconName: string;
  label: string;
  description: string;
}

export interface AideStubFAQ {
  question: string;
  answer: string;
}

export interface AideStub {
  slug: string;
  iconName: string;
  eyebrow: string;
  title: string;
  shortLabel: string;
  lead: string;
  amount: string;
  amountLabel: string;
  keyPoints: AideStubKeyPoint[];
  conditions: string[];
  cumulableWith: string[];
  faq: AideStubFAQ[];
  ctaLabel: string;
  ctaHref: string;
  secondaryCta?: { label: string; href: string };
}

export const AIDES_PARTICULIERS: AideStub[] = [
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'maprimerenov',
    iconName: 'piggy-bank',
    eyebrow: "L'aide de l'État pour vos travaux",
    title: "MaPrimeRénov'",
    shortLabel: 'MPR',
    lead: "L'aide principale pour vos travaux de rénovation. Son montant dépend de vos revenus, des travaux que vous faites et de l'économie d'énergie réalisée. Plus vos ressources sont modestes, plus l'aide est élevée.",
    amount: "Jusqu'à 63 000 €",
    amountLabel: 'Versement après travaux',
    keyPoints: [
      {
        iconName: 'piggy-bank',
        label: '4 profils selon vos revenus',
        description: "Votre niveau de ressources détermine le montant et le taux d'aide. Plus vos revenus sont modestes, plus l'aide est généreuse.",
      },
      {
        iconName: 'home',
        label: 'Logement de plus de 15 ans',
        description: "Résidence principale, occupée 8 mois par an, située en France métropolitaine ou Outre-mer.",
      },
      {
        iconName: 'hard-hat',
        label: 'Professionnel qualifié obligatoire',
        description: "Les travaux doivent être réalisés par un professionnel certifié.",
      },
      {
        iconName: 'file-text',
        label: 'Demande en ligne',
        description: "Dépôt avant signature du devis — on vous accompagne dans la démarche.",
      },
    ],
    conditions: [
      "Être propriétaire occupant, propriétaire bailleur ou copropriétaire",
      "Logement achevé depuis plus de 15 ans (sauf remplacement d'une chaudière fioul ou charbon)",
      "Résidence principale (occupation 8 mois par an minimum)",
      "Travaux réalisés par un professionnel certifié",
      "Demande déposée avant la signature du devis",
    ],
    cumulableWith: ['Prime énergie', 'Prêt sans intérêt (Éco-PTZ)', 'TVA à 5,5 %', 'Coup de pouce', 'Aides locales'],
    faq: [
      {
        question: "Quel est le montant en 2026 ?",
        answer:
          "Le montant dépend de votre profil de revenus et du type de travaux. Pour une pompe à chaleur air/eau, l'aide peut aller jusqu'à 5 000 € pour les ménages aux ressources les plus modestes. Utilisez notre calculateur pour une estimation personnalisée.",
      },
      {
        question: "Peut-on cumuler avec la prime énergie ?",
        answer:
          "Oui, l'aide de l'État se cumule avec la prime énergie, le prêt sans intérêt, la TVA à 5,5 % et les aides des collectivités locales. Mis bout à bout, ces dispositifs peuvent fortement faire baisser votre reste à charge.",
      },
      {
        question: "Dois-je passer par un accompagnateur ?",
        answer:
          "L'accompagnateur est obligatoire pour les rénovations globales visant un gain énergétique important. Il est facultatif mais recommandé pour les rénovations simples. Son coût peut être partiellement pris en charge.",
      },
    ],
    ctaLabel: "Calculer mon montant MaPrimeRénov'",
    ctaHref: '/particuliers/calculer-mes-aides',
    secondaryCta: { label: 'Voir les travaux éligibles', href: '/cee/fiches?secteur=BAR' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'cee',
    iconName: 'sparkles',
    eyebrow: "Prime financée par les fournisseurs d'énergie",
    title: 'Prime énergie',
    shortLabel: 'CEE',
    lead: "La prime énergie est versée par les fournisseurs d'énergie pour vous aider à financer vos travaux. Accessible à tous, sans condition de revenus. Le montant dépend du type de travaux réalisés.",
    amount: 'Selon travaux',
    amountLabel: 'Versement après travaux',
    keyPoints: [
      {
        iconName: 'sparkles',
        label: 'Sans condition de ressources',
        description: "Tous les ménages sont éligibles, y compris bailleurs et résidences secondaires.",
      },
      {
        iconName: 'file-text',
        label: 'Catalogue de travaux',
        description: "Chaque opération éligible (pompe à chaleur, isolation, ventilation…) correspond à une fiche technique.",
      },
      {
        iconName: 'hard-hat',
        label: 'Professionnel qualifié',
        description: "Un professionnel certifié est nécessaire pour bénéficier de la prime.",
      },
      {
        iconName: 'file-signature',
        label: 'Devis signé après la demande',
        description: "La demande de prime doit être faite avant la signature du devis, sinon le dossier est rejeté.",
      },
    ],
    conditions: [
      "Logement résidence principale ou secondaire achevé depuis plus de 2 ans",
      "Travaux éligibles correspondant à une opération du catalogue",
      "Professionnel certifié pour le poste concerné",
      "Demande de prime faite avant la signature du devis",
      "Conservation des justificatifs (devis, factures, attestation)",
    ],
    cumulableWith: ["MaPrimeRénov'", 'Prêt sans intérêt', 'TVA à 5,5 %', 'Coup de pouce'],
    faq: [
      {
        question: "Qui finance la prime énergie ?",
        answer:
          "Les vendeurs d'énergie (électricité, gaz, carburant) ont l'obligation de financer des économies d'énergie. Pour y arriver, ils vous aident à payer vos travaux via cette prime.",
      },
      {
        question: 'Puis-je choisir librement qui me verse la prime ?',
        answer:
          "Oui. Vous êtes libre de choisir l'offre la plus avantageuse. Vous n'avez pas besoin d'être client du fournisseur pour bénéficier de sa prime. L'essentiel : faites la demande avant de signer le devis.",
      },
      {
        question: "La prime énergie est-elle cumulable avec l'aide de l'État ?",
        answer:
          "Oui, intégralement. Les deux dispositifs sont conçus pour être cumulés. Pour une même opération (ex. pompe à chaleur), vous pouvez percevoir les deux aides, plus le prêt sans intérêt et la TVA réduite.",
      },
    ],
    ctaLabel: 'Estimer ma prime énergie',
    ctaHref: '/particuliers/calculer-mes-aides',
    secondaryCta: { label: 'Catalogue des fiches', href: '/cee/fiches?secteur=BAR' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'eco-ptz',
    iconName: 'banknote',
    eyebrow: 'Prêt à taux zéro',
    title: 'Éco-PTZ',
    shortLabel: 'éco-PTZ',
    lead: "Un prêt à taux zéro pour financer vos travaux sans payer d'intérêts. Accessible à tous les propriétaires, sans condition de ressources. Vous remboursez uniquement le capital emprunté.",
    amount: "Jusqu'à 50 000 €",
    amountLabel: 'Prêt sans intérêt sur 20 ans max',
    keyPoints: [
      {
        iconName: 'banknote',
        label: 'Taux zéro réel',
        description: "Vous remboursez uniquement le capital. Aucun intérêt, aucun frais de dossier.",
      },
      {
        iconName: 'home',
        label: 'Sans condition de ressources',
        description: "Accessible à tous les propriétaires (occupants ou bailleurs), peu importe leurs revenus.",
      },
      {
        iconName: 'gauge',
        label: "Jusqu'à 50 000 € pour une rénovation globale",
        description: "Pour les rénovations d'ampleur avec un gain énergétique important. Plafonds inférieurs pour un geste isolé.",
      },
      {
        iconName: 'wallet',
        label: 'Cumulable avec les aides',
        description: "Le cumul réduit votre endettement tout en gardant le bénéfice des aides directes.",
      },
    ],
    conditions: [
      "Logement utilisé comme résidence principale",
      "Logement achevé depuis plus de 2 ans à la date de début des travaux",
      "Travaux d'amélioration énergétique éligibles (isolation, chauffage performant, etc.)",
      "Travaux réalisés par un professionnel certifié",
      "Prêt accordé par une banque conventionnée par l'État",
    ],
    cumulableWith: ["MaPrimeRénov'", 'Prime énergie', 'TVA à 5,5 %', 'Aides locales'],
    faq: [
      {
        question: "Quel est le plafond du prêt en 2026 ?",
        answer:
          "Le plafond dépend du type de travaux : 7 000 € pour des fenêtres seules, 15 000 € pour un geste classique, 25 000 € pour 2 actions, 30 000 € pour 3 actions ou plus, et jusqu'à 50 000 € pour une rénovation globale.",
      },
      {
        question: 'Quelle banque accorde ce prêt ?',
        answer:
          "Toutes les banques ayant signé une convention avec l'État peuvent l'accorder. Vous présentez votre dossier à la banque de votre choix, qui instruit la demande comme un prêt classique.",
      },
      {
        question: "Ce prêt est-il cumulable avec l'aide de l'État ?",
        answer:
          "Oui, intégralement. Le prêt peut même financer la part restant à votre charge après déduction des aides. C'est l'outil de complément naturel pour rénover sans toucher à votre trésorerie.",
      },
    ],
    ctaLabel: 'Simuler mon plan de financement',
    ctaHref: '/particuliers/calculer-mes-aides',
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'tva-reduite',
    iconName: 'percent',
    eyebrow: 'TVA réduite',
    title: 'TVA à 5,5 %',
    shortLabel: 'TVA 5,5 %',
    lead: "Sur vos travaux de rénovation, la TVA passe de 20 % à 5,5 %. L'économie est automatique : votre artisan applique le bon taux directement sur la facture. Aucune démarche complexe de votre côté.",
    amount: '5,5 % vs 20 %',
    amountLabel: 'Économie de 14,5 points sur la facture',
    keyPoints: [
      {
        iconName: 'percent',
        label: 'Appliqué automatiquement',
        description: "L'artisan applique le taux réduit dès la facturation. Aucune démarche complexe de votre côté.",
      },
      {
        iconName: 'home',
        label: 'Logement de plus de 2 ans',
        description: 'Résidence principale ou secondaire, propriétaire ou locataire.',
      },
      {
        iconName: 'hammer',
        label: 'Sur la pose et le matériel',
        description: "Le taux réduit s'applique aussi bien aux équipements qu'à la main-d'œuvre, sans condition de certification.",
      },
      {
        iconName: 'file-signature',
        label: 'Attestation à signer',
        description: "Vous signez une attestation simplifiée fournie par l'artisan.",
      },
    ],
    conditions: [
      'Logement achevé depuis plus de 2 ans',
      "Travaux d'amélioration de la qualité énergétique (isolation, chauffage performant, etc.)",
      "Réalisation par un professionnel (artisan, entreprise du bâtiment)",
      'Attestation simplifiée signée par le client et remise à l\'artisan',
      'Conservation des justificatifs pendant 5 ans',
    ],
    cumulableWith: ["MaPrimeRénov'", 'Prime énergie', 'Prêt sans intérêt', 'Coup de pouce'],
    faq: [
      {
        question: 'Quels travaux bénéficient de la TVA à 5,5 % ?',
        answer:
          "Les travaux d'amélioration énergétique : isolation thermique (combles, murs, planchers), équipements de chauffage performants, équipements utilisant des énergies renouvelables, régulation du chauffage, ventilation double flux.",
      },
      {
        question: 'Faut-il un professionnel certifié ?',
        answer:
          "Non. Contrairement aux autres aides, la TVA réduite ne nécessite pas de certification particulière. Toutefois, si vous souhaitez cumuler avec les autres aides, le professionnel devra être certifié.",
      },
      {
        question: "Que faire si l'artisan applique 20 % au lieu de 5,5 % ?",
        answer:
          "Vous pouvez demander une facture rectificative à l'artisan. Si les travaux respectent les conditions (logement de plus de 2 ans, travaux énergétiques, attestation signée), le taux réduit est dû.",
      },
    ],
    ctaLabel: 'Calculer mon économie totale',
    ctaHref: '/particuliers/calculer-mes-aides',
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'coup-de-pouce',
    iconName: 'flame',
    eyebrow: 'Prime renforcée sur certains travaux',
    title: 'Coup de pouce',
    shortLabel: 'Coup de pouce',
    lead: "Un bonus qui s'ajoute à votre prime énergie pour certains travaux prioritaires : remplacer un vieux chauffage, isoler vos combles ou vos planchers. Le montant est plus élevé si vos revenus sont modestes.",
    amount: 'Prime majorée',
    amountLabel: 'Prime renforcée pour travaux ciblés',
    keyPoints: [
      {
        iconName: 'flame',
        label: 'Sur travaux ciblés',
        description: "Remplacement d'un chauffage fossile par une pompe à chaleur, isolation des combles ou planchers, rénovation globale.",
      },
      {
        iconName: 'piggy-bank',
        label: 'Montant selon vos revenus',
        description: "Montants majorés pour les ménages aux revenus modestes et très modestes.",
      },
      {
        iconName: 'sparkles',
        label: "Cumulable avec l'aide de l'État",
        description: "Le Coup de pouce s'ajoute aux autres aides pour maximiser le total perçu.",
      },
      {
        iconName: 'file-signature',
        label: 'Bonus versé par un partenaire agréé',
        description: "Le bonus est versé via un signataire de la charte qualité officielle.",
      },
    ],
    conditions: [
      "Opérations éligibles définies par la réglementation",
      "Demande de prime faite avant la signature du devis",
      "Professionnel certifié pour le poste de travaux concerné",
      "Versement via un partenaire signataire de la charte qualité",
      "Conservation des pièces justificatives",
    ],
    cumulableWith: ["MaPrimeRénov'", 'Prime énergie de base', 'Prêt sans intérêt', 'TVA à 5,5 %'],
    faq: [
      {
        question: 'Quelle différence avec la prime énergie classique ?',
        answer:
          "La prime énergie classique couvre toute opération éligible au tarif standard. Le Coup de pouce cible certaines opérations prioritaires (remplacement chauffage fossile, isolation) avec un montant majoré, surtout pour les ménages modestes.",
      },
      {
        question: 'Quels travaux sont concernés ?',
        answer:
          "Les principaux : remplacement d'un chauffage fossile (pompe à chaleur, biomasse), isolation (combles, planchers) et rénovation globale avec un gain énergétique important. La liste évolue régulièrement.",
      },
      {
        question: "Le Coup de pouce est-il cumulable avec l'aide de l'État ?",
        answer:
          "Oui. Le Coup de pouce est un bonus de la prime énergie, il se cumule avec l'aide de l'État. Pour les ménages modestes, le cumul peut couvrir une part très importante du coût des travaux.",
      },
    ],
    ctaLabel: 'Vérifier mon éligibilité',
    ctaHref: '/particuliers/calculer-mes-aides',
  },
];

export function getAideStub(slug: string): AideStub | undefined {
  return AIDES_PARTICULIERS.find((a) => a.slug === slug);
}
